<template>
    <wizard 
        :entity-name="$t('account.entityName')"
        :step-index="0"
        ref="wizard"
        :data-loading="loading || validationLoading"
        data-test-name="account-add"
        @finish="onFinish"
        @on-cancel="onCancel"
    >

        <wizard-step 
            icon="vc-icon-extensions"
            v-loading="loading"
            :name="$t('report.wizard.generalStep')"
            :title="$t('account.wizard.generalStep')"
            class="flex items-center"
            :before-leave="validateGeneralStep"
        >
            <template v-slot:summary>
                <wizard-summary-row
                    :label="`${$t('report.name')}:`"
                    :value="reportData.ReportName"
                />
                <wizard-summary-row
                    :label="`${$t('report.status')}:`"
                    :value="reportStatusIDText"
                />
                <wizard-summary-row
                    :label="`${$t('report.layout')}:`"
                    :value="reportLayout"
                />
                <wizard-summary-row
                    v-show="reportData.ReportConf.timezone.timezoneStatus"
                    :label="`${$t('general.timezone')}:`"
                    :value="reportTimezoneValue"
                />
            </template>

            <create-report-step-first ref="generalStep" :reportData="reportData" @updateReportData="updateReportData" />

        </wizard-step>

        <wizard-step
            icon="vc-icon-id"
            v-loading="loading"
            :name="$t('report.wizard.idStep')"
            :title="$t('report.wizard.idStep')"
            class="flex items-center"
            :before-leave="validateIdStep"
        >

            <template v-slot:summary>
                <wizard-summary-row
                    :label="`${$t('report.wizard.idStep')}:`"
                    :value="reportData.ReportItemList.length"
                />
            </template>

            <create-report-step-second ref="stepTwo" :reportData="reportData" @updateReportData="updateReportData" />
        </wizard-step>

        <wizard-step
            icon="vc-icon-filter"
            v-loading="loading"
            :name="$t('report.wizard.finalStep')"
            :title="$t('report.wizard.finalStep')"
        >

            <template v-slot:summary>
                <wizard-summary-row
                    :label="`${$t('report.wizard.finalStep')}:`"
                    :value="reportData.ReportTriggerList.length"
                />
            </template>

            <create-report-step-third ref="finalStep" :reportData="reportData" @pushDataInReportTrigger="pushDataInReportTrigger" />
        </wizard-step>

    </wizard>
</template>

<script>
    import { reportApi } from '@/modules/reports/services/reportService'
    import { Notification, Tooltip } from 'element-ui'
    import { setDefaultReportData } from '@/enum/reportStaticData'
    import { REPORT_STATUS_ID_ENABLED } from '@/enum/reportStatuses'
    import momentTimezone from 'moment-timezone'

    export default {
        props: {
            loading: {
                type: Boolean,
                default: false
            }
        },
        components: {
            CreateReportStepFirst: () => import('./CreateReportStepFirst.vue'),
            CreateReportStepSecond: () => import('./CreateReportStepSecond.vue'),
            CreateReportStepThird: () => import('./CreateReportStepThird.vue'),
            Wizard: () => import('@/modules/reports/components/wizard/Wizard'),
            WizardStep: () => import('@/modules/reports/components/wizard/WizardStep'),
            WizardSummaryRow: () => import('@/modules/reports/components/wizard/WizardSummaryRow'),
            [Tooltip.name]: Tooltip
        },
        data() {
            return {
                validationLoading: false,
                reportData: setDefaultReportData()
            }
        },
        computed: {
            currentAccountId () {
                return this.$store.state.entities.selectedAccountID
            },
            selectedLayout() {
                return this.$store.state.layout.activeLayout.LayoutID
            },
            reportStatusIDText () {
                return this.reportData.ReportStatusID === REPORT_STATUS_ID_ENABLED ? this.$t('report.wizard.enabled') : this.$t('report.switch.disable')
            },
            activeAccountLayouts() {
                return this.$store.getters['layout/getActiveLayouts']
            },
            reportLayout () {
                return this.activeAccountLayouts.find(el => el.LayoutID === this.reportData.LayoutID) ?
                    this.activeAccountLayouts.find(el => el.LayoutID === this.reportData.LayoutID).LayoutName : ''
            },
            reportTimezoneValue () {
                if (!this.reportData.ReportConf.timezone.timezoneName) {
                    return ''
                }
                const timeZones = momentTimezone.tz.names();
                const offsetTmz=[];

                for(const i in timeZones) {
                    offsetTmz.push({
                        value: timeZones[i],
                        label: " (GMT"+momentTimezone.tz(timeZones[i]).format('Z')+") " + timeZones[i]
                    });
                }

                return offsetTmz.find(el => el.value === this.reportData.ReportConf.timezone.timezoneName).label
            }
        },
        methods: {
            validateGeneralStep() {
                return this.$refs['generalStep'].validate()
            },
            validateIdStep() {
                const isValid = this.$refs['stepTwo'].$refs.widgetTable.validate()
                if (!isValid) {
                    Notification.warning(this.$t('report.schedule.needAddAtLeastOneWidget'))
                    return
                }
                return isValid
            },
            async onFinish() {
                const isValid = this.$refs['finalStep'].validate()
                if (!isValid) {
                    Notification.warning(this.$t('report.schedule.needAddAtLeastOneSchedule'))
                    return
                }

                const data = await this.reportData
                data.AccountID = this.currentAccountId
                try {
                    await reportApi.store(data)
                    this.$emit('created-new-report');
                } catch (errors) {
                    console.error(errors);
                }
            },
            async onCancel() {
                this.$emit('go-back');
            },
            updateReportData (data) {
                Object.keys(data).forEach(el => {
                    this.reportData[el] = data[el]
                })
            },
            pushDataInReportTrigger (data) {
                this.reportData.ReportTriggerList.push(data)
            }
        }
    }
</script>
